import { DEFAULT_FILM_FORMAT, FilmFormatId } from '@/content/instantFilm'
import { assertIsoDate, isoDateParts, type IsoDate } from '@/lib/isoDate'
import type { Size } from '@/core/geometry'

export { assertIsoDate, toIsoDate, type IsoDate } from '@/lib/isoDate'

export enum FrameStyleId {
  Polaroid = 'polaroid',
  Plain = 'plain',
}

export enum PaperPresetId {
  White = 'white',
  Warm = 'warm',
  Cream = 'cream',
  Black = 'black',
  Sand = 'sand',
  Sage = 'sage',
}

export interface PaperColorPreset {
  id: PaperPresetId
  color: string
}

export const PAPER_COLOR_PRESETS = [
  { id: PaperPresetId.White, color: '#ffffff' },
  { id: PaperPresetId.Warm, color: '#fbf8f2' },
  { id: PaperPresetId.Cream, color: '#f5efe3' },
  { id: PaperPresetId.Black, color: '#111111' },
  { id: PaperPresetId.Sand, color: '#e8dcc8' },
  { id: PaperPresetId.Sage, color: '#cfd8cc' },
] as const satisfies readonly PaperColorPreset[]

export const DEFAULT_PAPER_COLOR = '#ffffff'

export enum DateFormatId {
  DayMonthYear = 'dd.MM.yyyy',
  MonthDayYear = 'MM/dd/yyyy',
  LongMonth = 'MMM d, yyyy',
  FilmStamp = "''yy M d",
}

export enum PhotoPlacement {
  Fill = 'fill',
  Free = 'free',
}

export interface FreePlacement {
  scale: number
  offsetX: number
  offsetY: number
}

export interface PolaroidFrameParams {
  styleId: FrameStyleId.Polaroid
  formatId: FilmFormatId
  paperColor: string
  placement: PhotoPlacement
  free: FreePlacement | null
  windowShadow: boolean
  printShadow: boolean
  date: IsoDate | null
  dateFormatId: DateFormatId
  showWrittenDate: boolean
  showStampDate: boolean
  caption: string
}

export interface PlainFrameParams {
  styleId: FrameStyleId.Plain
  color: string
  borderMm: number
}

export type FrameParams = PolaroidFrameParams | PlainFrameParams

export const CAPTION_MAX_LENGTH = 40
export const FREE_PLACEMENT_MIN_SCALE = 0.2
export const FREE_PLACEMENT_MAX_SCALE = 1
export const FREE_PLACEMENT_MAX_OFFSET = 0.5
export const DEFAULT_PLAIN_FRAME_COLOR = '#ffffff'
export const DEFAULT_PLAIN_FRAME_BORDER_MM = 3
export const MIN_PLAIN_FRAME_BORDER_MM = 0
export const MAX_PLAIN_FRAME_BORDER_MM = 20
export const PLAIN_FRAME_INNER_SHORT_EDGE_MM = 100

export function plainFrameInnerSize(targetSize: Size, borderMm: number): Size {
  if (!Number.isFinite(targetSize.width) || !Number.isFinite(targetSize.height)
    || targetSize.width <= 0 || targetSize.height <= 0) {
    throw new Error('Plain frame target size must be finite and positive')
  }
  if (!Number.isFinite(borderMm)
    || borderMm < MIN_PLAIN_FRAME_BORDER_MM
    || borderMm > MAX_PLAIN_FRAME_BORDER_MM) {
    throw new Error(`Plain frame border must be from ${MIN_PLAIN_FRAME_BORDER_MM} to ${MAX_PLAIN_FRAME_BORDER_MM} mm`)
  }
  const outerShortEdgeMm = PLAIN_FRAME_INNER_SHORT_EDGE_MM + borderMm * 2
  const borderPixels = Math.min(targetSize.width, targetSize.height) * borderMm / outerShortEdgeMm
  return {
    width: targetSize.width - borderPixels * 2,
    height: targetSize.height - borderPixels * 2,
  }
}

export const WINDOW_SHADOW = {
  blurMm: 1.2,
  spreadMm: 0.4,
  alpha: 0.35,
} as const

export const PRINT_SHADOW = {
  offsetYMm: 1.5,
  blurMm: 3,
  alpha: 0.28,
} as const

const HEX_COLOR_PATTERN = /^#[\da-f]{6}$/i
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

export function formatDate(date: IsoDate, format: DateFormatId): string {
  const { year, month, day } = isoDateParts(date)
  const paddedMonth = String(month).padStart(2, '0')
  const paddedDay = String(day).padStart(2, '0')
  switch (format) {
    case DateFormatId.DayMonthYear:
      return `${paddedDay}.${paddedMonth}.${year}`
    case DateFormatId.MonthDayYear:
      return `${paddedMonth}/${paddedDay}/${year}`
    case DateFormatId.LongMonth: {
      const monthName = MONTH_NAMES[month - 1]
      if (!monthName) throw new Error(`Date month ${month} has no configured label`)
      return `${monthName} ${day}, ${year}`
    }
    case DateFormatId.FilmStamp:
      return `'${String(year).slice(-2)} ${month} ${day}`
    default:
      throw new Error(`Unknown date format: "${String(format)}"`)
  }
}

export function assertHexColor(value: string, label: string): void {
  if (!HEX_COLOR_PATTERN.test(value)) throw new Error(`${label} must be a six-digit hex color`)
}

export function inkColorFor(paperColor: string): string {
  assertHexColor(paperColor, 'Paper color')
  const red = Number.parseInt(paperColor.slice(1, 3), 16)
  const green = Number.parseInt(paperColor.slice(3, 5), 16)
  const blue = Number.parseInt(paperColor.slice(5, 7), 16)
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255
  return luminance < 0.45 ? '#f2f2f2' : '#1f1f1f'
}

export function validatePolaroidFrameParams(params: PolaroidFrameParams): void {
  if (params.styleId !== FrameStyleId.Polaroid) {
    throw new Error(`Unknown frame style: "${String(params.styleId)}"`)
  }
  if (!Object.values(FilmFormatId).includes(params.formatId)) {
    throw new Error(`Unknown film format: "${String(params.formatId)}"`)
  }
  assertHexColor(params.paperColor, 'Frame paper color')
  if (!Object.values(PhotoPlacement).includes(params.placement)) {
    throw new Error(`Unknown photo placement: "${String(params.placement)}"`)
  }
  if (params.placement === PhotoPlacement.Fill && params.free !== null) {
    throw new Error('Fill photo placement requires free placement to be null')
  }
  if (params.placement === PhotoPlacement.Free && params.free === null) {
    throw new Error('Free photo placement requires free placement parameters')
  }
  if (params.free) {
    const { scale, offsetX, offsetY } = params.free
    if (!Number.isFinite(scale) || scale < FREE_PLACEMENT_MIN_SCALE || scale > FREE_PLACEMENT_MAX_SCALE) {
      throw new Error(`Free photo scale must be from ${FREE_PLACEMENT_MIN_SCALE} to ${FREE_PLACEMENT_MAX_SCALE}`)
    }
    if (!Number.isFinite(offsetX) || !Number.isFinite(offsetY)
      || Math.abs(offsetX) > FREE_PLACEMENT_MAX_OFFSET || Math.abs(offsetY) > FREE_PLACEMENT_MAX_OFFSET) {
      throw new Error(`Free photo offsets must be from -${FREE_PLACEMENT_MAX_OFFSET} to ${FREE_PLACEMENT_MAX_OFFSET}`)
    }
  }
  if (typeof params.windowShadow !== 'boolean' || typeof params.printShadow !== 'boolean') {
    throw new Error('Frame shadow settings must be boolean values')
  }
  if (params.date !== null) assertIsoDate(params.date)
  if (!Object.values(DateFormatId).includes(params.dateFormatId)) {
    throw new Error(`Unknown date format: "${String(params.dateFormatId)}"`)
  }
  if (typeof params.showWrittenDate !== 'boolean' || typeof params.showStampDate !== 'boolean') {
    throw new Error('Frame date visibility settings must be boolean values')
  }
  if (typeof params.caption !== 'string' || params.caption.length > CAPTION_MAX_LENGTH) {
    throw new Error(`Frame caption must be at most ${CAPTION_MAX_LENGTH} characters`)
  }
}

export function validatePlainFrameParams(params: PlainFrameParams): void {
  if (params.styleId !== FrameStyleId.Plain) {
    throw new Error(`Plain frame parameters require style "${FrameStyleId.Plain}"`)
  }
  assertHexColor(params.color, 'Plain frame color')
  if (!Number.isFinite(params.borderMm)
    || params.borderMm < MIN_PLAIN_FRAME_BORDER_MM
    || params.borderMm > MAX_PLAIN_FRAME_BORDER_MM) {
    throw new Error(`Plain frame border must be from ${MIN_PLAIN_FRAME_BORDER_MM} to ${MAX_PLAIN_FRAME_BORDER_MM} mm`)
  }
}

export function validateFrameParams(params: FrameParams): void {
  switch (params.styleId) {
    case FrameStyleId.Polaroid:
      validatePolaroidFrameParams(params)
      return
    case FrameStyleId.Plain:
      validatePlainFrameParams(params)
      return
    default:
      throw new Error(`Unknown frame style: "${String((params as { styleId: unknown }).styleId)}"`)
  }
}

export function createDefaultPlainFrameParams(): PlainFrameParams {
  const params: PlainFrameParams = {
    styleId: FrameStyleId.Plain,
    color: DEFAULT_PLAIN_FRAME_COLOR,
    borderMm: DEFAULT_PLAIN_FRAME_BORDER_MM,
  }
  validatePlainFrameParams(params)
  return params
}

export function createDefaultPolaroidFrameParams(date: IsoDate | null = null): PolaroidFrameParams {
  const params: PolaroidFrameParams = {
    styleId: FrameStyleId.Polaroid,
    formatId: DEFAULT_FILM_FORMAT,
    paperColor: DEFAULT_PAPER_COLOR,
    placement: PhotoPlacement.Fill,
    free: null,
    windowShadow: true,
    printShadow: true,
    date,
    dateFormatId: DateFormatId.DayMonthYear,
    showWrittenDate: date !== null,
    showStampDate: false,
    caption: '',
  }
  validatePolaroidFrameParams(params)
  return params
}
